import type { ComponentProps, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

import DefaultTable from "./DefaultTable";

type DefaultTableProps = ComponentProps<typeof DefaultTable>;

export interface PaginatedTableDetails {
	take: number;
	page: number;
	orderBy: string;
	order: DefaultTableProps["order"];
	search?: string;
}

interface PaginatedTableProps {
	defaultOrder: string;
	searchValue?: string;
	getRows: (details: PaginatedTableDetails) => {
		data?: { rows: DefaultTableProps["rows"]; count: number };
		isLoading: boolean;
	};
	Skeleton: ReactNode;
	[key: string]: unknown;
}

export default function PaginatedTable(props: PaginatedTableProps) {
	const ref = useRef<{ searchTimeout?: ReturnType<typeof setTimeout> }>({});
	const [details, setDetails] = useState<PaginatedTableDetails>({
		take: 10,
		page: 0,
		orderBy: props.defaultOrder,
		order: "DESC",
		search: props.searchValue,
	});

	const updateDetails = (newDetails: Partial<PaginatedTableDetails>) => {
		setDetails({
			...details,
			...newDetails,
		});
	};

	useEffect(() => {
		clearTimeout(ref.current.searchTimeout);
		ref.current.searchTimeout = setTimeout(() => {
			setDetails((prev) => ({
				...prev,
				search: props.searchValue,
				page: 0,
			}));
		}, 400);
		return () => clearTimeout(ref.current.searchTimeout);
	}, [props.searchValue]);

	const { data: { rows, count } = { rows: [], count: 0 }, isLoading } =
		props.getRows(details);

	if (isLoading) {
		return props.Skeleton;
	}

	return (
		<DefaultTable
			{...(props as Partial<DefaultTableProps>)}
			{...details}
			rows={rows}
			count={count}
			setDetails={updateDetails}
		/>
	);
}
